"use client";

import { useEffect, type ReactNode } from "react";
import { useRouter, usePathname } from "next/navigation";
import Image from "next/image";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Header from "@/components/dashboard/Header";
import { Loader2 } from "lucide-react";

// (main) agrupa /dashboard y /crear — únicas rutas de la app que necesitan sesión de
// Firebase Auth en el cliente en todo momento. AuthProvider se monta aquí (y no en el
// root layout) para que las rutas públicas no carguen el SDK de Auth.
export default function MainLayout({ children }: { children: ReactNode }) {
  return (
    <AuthProvider>
      <MainLayoutInner>{children}</MainLayoutInner>
    </AuthProvider>
  );
}

function MainLayoutInner({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    console.log(`[MainLayout] useEffect triggered. Path: ${pathname}, User: ${user?.uid || null}, AuthLoading: ${authLoading}`);

    if (!authLoading && !user) {
      if (pathname !== "/login" && pathname !== "/finish-login") {
        console.log('[MainLayout] Not authLoading and no user, redirecting to /login');
        router.push("/login");
      }
    }
  }, [user, authLoading, router, pathname]);
  
  // Mostrar loader si authLoading es true, O si no hay usuario Y no estamos ya en login/finish-login
  // Y el authLoading ha terminado (esto es para evitar un flash del loader si el usuario ya está presente rápidamente)
  if (authLoading || (!user && !authLoading && pathname !== "/login" && pathname !== "/finish-login")) {
     // DIAGNÓSTICO TEMPORAL (timestamp agregado) — quitar junto con los logs de "[SuccessScreen debug]".
     console.log(`[MainLayout] Showing loader. AuthLoading: ${authLoading}, User: ${user?.uid || null}, Pathname: ${pathname}, timestamp: ${Date.now()}`);
    // Splash de DevMonkeys en vez de pantalla en blanco — el archivo tiene su propio
    // espacio negro reservado arriba (ver public/devmonkeys/devmonkeys_waiting_2.webp),
    // ahí es donde flota el spinner. object-contain (NO object-cover): en cualquier
    // ventana más ancha que alta (escritorio, o un celular en horizontal) object-cover
    // recortaba casi toda la ilustración por abajo dejando solo el vacío negro de
    // arriba — con la imagen nunca se llegaba a ver aunque ya estuviera descargada.
    // contain siempre muestra la imagen completa; el bg-[#111217] (mismo tono que el
    // negro de la imagen, ver el color real con Python/Pillow) hace invisible cualquier
    // franja de "letterbox" que aparezca a los lados o arriba/abajo. object-bottom (no
    // object-top): en pantallas altas y angostas (celular vertical, el caso más común)
    // contain dejaba la imagen "flotando" arriba con un hueco muerto abajo — anclada
    // abajo la imagen queda pegada al piso de la pantalla y el hueco (si lo hay) sube
    // arriba, que es además donde ya vive el spinner.
    // `unoptimized`: mismo motivo que el resto de /devmonkeys — ver next.config.ts.
    return (
      <div className="relative h-screen w-screen overflow-hidden bg-[#111217]">
        <Image
          src="/devmonkeys/devmonkeys_waiting_2.webp"
          alt=""
          fill
          priority
          unoptimized
          className="object-contain object-bottom"
        />
        <div className="absolute inset-x-0 top-[22%] z-10 flex items-center justify-center gap-3 px-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-lg font-medium text-white/90">Cargando sesión...</p>
        </div>
      </div>
    );
  }
  
  // Si hay un usuario, o si no hay usuario pero estamos en una página pública permitida
  // y el auth ha terminado de cargar.
  if (user || (!user && !authLoading && (pathname === "/login" || pathname === "/finish-login"))) {
    console.log('[MainLayout] User is present or on public auth page, rendering layout content.');
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1">{children}</main>
         <footer className="py-6 md:px-8 md:py-0 bg-background border-t">
          <div className="container flex flex-col items-center justify-between gap-4 md:min-h-20 md:flex-row">
            <p className="text-balance text-center text-sm leading-loose text-muted-foreground md:text-left">
              Desarrollado por SnapSite de .Site RTSI. Crea tu perfil con facilidad.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Fallback por si alguna condición no se cubre, aunque la lógica anterior debería ser exhaustiva.
  // Podría ser útil si se añaden más rutas públicas que no sean /login o /finish-login.
  console.log(`[MainLayout] Fallback rendering. AuthLoading: ${authLoading}, Pathname: ${pathname}. Rendering children (or caught by redirect).`);
  return <>{children}</>;
}
